import { useCallback, useMemo } from 'react';
import { usePeopleContext } from '@shared/context/people';
import type { SmartAudienceOption } from './smartAudience.types';
import { isSameSmartRule } from './smartAudience.utils';

/**
 * The PeopleContext half of a smart tag, shared by both picker buttons.
 *
 * `applySmartAudience` returns true when the submit was a smart tag and is fully handled, so the caller
 * skips writing guests. Otherwise it clears a smart tag the host could see and chose to drop — but
 * leaves one the picker never showed (an option the server stopped offering), because a Done click on
 * an empty dialog is not a decision about an audience the host couldn't see.
 */
export const useSmartAudienceSubmit = (smartAudiences: ReadonlyArray<SmartAudienceOption> | undefined) => {
  const { smartRule, setSmartRule } = usePeopleContext();

  const initialSmartAudienceId = useMemo(() => smartAudiences?.find(option => isSameSmartRule(option.rule, smartRule))?.id ?? null, [smartAudiences, smartRule]);

  const applySmartAudience = useCallback(
    (smartAudience: SmartAudienceOption | null): boolean => {
      if (smartAudience) {
        setSmartRule(smartAudience.rule);
        return true;
      }
      if (initialSmartAudienceId) setSmartRule(null);
      return false;
    },
    [initialSmartAudienceId, setSmartRule],
  );

  return { initialSmartAudienceId, applySmartAudience };
};
